import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { subirArchivo } from "../utils/upload";
import MiniaturaArchivo from "../components/MiniaturaArchivo";
import { Ayuda } from "../context/AyudaContext";
import { UNIDADES } from "../utils/unidades";
import { separarCantidadDelNombre } from "../utils/nombreProducto";
import { EncabezadoDetalle, TEMA, tarjetaTema, thTema } from "../components/TemaDetalle";
import Cargando from "../components/Cargando";


const filaVacia = (prioridad = "normal") => ({
  descripcion_producto: "",
  cantidad: "",
  unidad: "",
  descripcion_detallada: "",
  prioridad,
  proveedor_ids: [],
  archivo_referencia: "",
  // Solo para mostrar el adjunto antes de guardar; el servidor no los usa.
  archivo_nombre: "",
  vista_previa: "",
  subiendoArchivo: false,
});

// Una imagen se ve en miniatura; un PDF solo se nombra (todavía no está en la solicitud guardada).
function AdjuntoProducto({ item }) {
  if (item.vista_previa) return <MiniaturaArchivo nombreArchivo={item.archivo_referencia} vistaPrevia={item.vista_previa} />;
  return <span style={{ fontSize: "1.3rem" }} title={item.archivo_nombre || "Archivo adjunto"}>📄</span>;
}

const nuevoProveedorVacio ={ nombre_empresa: "", telefono: "", correo: "" };

function NuevaSolicitud() {
  const navigate = useNavigate();
  const proximoId = useRef(1);
  const archivoInputRef = useRef(null);

  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  // El área de un solicitante la fija su perfil (Gestionar Usuarios) — no
  // puede elegir otra, para que nunca cree una solicitud a nombre de un área
  // que no es la suya. Compras/aprobador/admin sí pueden elegir cualquiera
  // (por ejemplo, para pedir algo a nombre de otra área).
  const esSolicitante = usuario?.rol === "solicitante";
  const areaFijaId = esSolicitante ? usuario?.area_id || "" : "";
  // Todos los usuarios arrancan con el área de su perfil ya elegida; los que no son solicitantes
  // (compras, aprobador, admin) pueden cambiarla si piden a nombre de otra área.
  const areaPorDefecto = usuario?.area_id ? String(usuario.area_id) : "";

  const [areas, setAreas] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [areaId, setAreaId] = useState(areaFijaId ? String(areaFijaId) : areaPorDefecto);
  const [edificioIds, setEdificioIds] = useState([]);
  const [edificioDropdownAbierto, setEdificioDropdownAbierto] = useState(false);
  const edificioDropdownRef = useRef(null);
  const [descripcionRequerimientoGlobal, setDescripcionRequerimientoGlobal] = useState("");
  const [prioridadGlobal, setPrioridadGlobal] = useState("normal");

  const [itemsGuardados, setItemsGuardados] = useState([]);
  const [itemActual, setItemActual] = useState(filaVacia());
  const [editandoId, setEditandoId] = useState(null);
  const [arrastrandoArchivo, setArrastrandoArchivo] = useState(false);
  const descripcionRequerimientoRef = useRef(null);
  const descripcionProductoRef = useRef(null);
  const descripcionDetalladaRef = useRef(null);

  // "Descripción del producto" y "Descripción detallada" crecen con el
  // texto en vez de desbordarse hacia los lados o hacia abajo — se
  // recalcula tanto al escribir como al limpiarse (agregar a la lista,
  // cancelar edición, etc.). La detallada además tiene un tope de alto
  // (puede traer hasta 1000 caracteres pegados) y de ahí en adelante
  // scrollea por dentro en vez de seguir creciendo sin límite.
  useEffect(() => {
    const el = descripcionProductoRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [itemActual.descripcion_producto]);

  useEffect(() => {
    const el = descripcionDetalladaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [itemActual.descripcion_detallada]);

  useEffect(() => {
    const el = descripcionRequerimientoRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [descripcionRequerimientoGlobal]);

  const [proveedorDropdownAbierto, setProveedorDropdownAbierto] = useState(false);
  const proveedorDropdownRef = useRef(null);
  const [formNuevoProveedorAbierto, setFormNuevoProveedorAbierto] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState(nuevoProveedorVacio);
  const [creandoProveedor, setCreandoProveedor] = useState(false);

  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    Promise.all([apiFetch("/api/areas"), apiFetch("/api/edificios"), apiFetch("/api/proveedores")])
      .then(([areasData, edificiosData, proveedoresData]) => {
        setAreas(areasData || []);
        setEdificios(edificiosData || []);
        setProveedores(proveedoresData || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargandoCatalogos(false));
  }, []);

  useEffect(() => {
    if (!edificioDropdownAbierto) return;
    const cerrarSiEsAfuera = (e) => {
      if (edificioDropdownRef.current && !edificioDropdownRef.current.contains(e.target)) {
        setEdificioDropdownAbierto(false);
      }
    };
    document.addEventListener("mousedown", cerrarSiEsAfuera);
    return () => document.removeEventListener("mousedown", cerrarSiEsAfuera);
  }, [edificioDropdownAbierto]);

  useEffect(() => {
    if (!proveedorDropdownAbierto) return;
    const cerrarSiEsAfuera = (e) => {
      if (proveedorDropdownRef.current && !proveedorDropdownRef.current.contains(e.target)) {
        setProveedorDropdownAbierto(false);
        setFormNuevoProveedorAbierto(false);
      }
    };
    document.addEventListener("mousedown", cerrarSiEsAfuera);
    return () => document.removeEventListener("mousedown", cerrarSiEsAfuera);
  }, [proveedorDropdownAbierto]);

  const alternarEdificio = (edificioId) => {
    setEdificioIds((prev) =>
      prev.includes(edificioId) ? prev.filter((id) => id !== edificioId) : [...prev, edificioId]
    );
  };

  const actualizarCampo = (campo, valor) => {
    setItemActual((prev) => ({ ...prev, [campo]: valor }));
  };

  const alternarProveedor = (proveedorId) => {
    setItemActual((prev) => ({
      ...prev,
      proveedor_ids: prev.proveedor_ids.includes(proveedorId)
        ? prev.proveedor_ids.filter((id) => id !== proveedorId)
        : [...prev.proveedor_ids, proveedorId],
    }));
  };

  // Alta rápida de un proveedor que no está en el catálogo — se crea al
  // toque (no se espera a enviar toda la solicitud) para poder marcarlo de
  // una vez junto a los demás proveedores recomendados de este producto.
  const crearProveedorRapido = async () => {
    if (!nuevoProveedor.nombre_empresa.trim()) {
      setError("Escribe el nombre de la empresa del proveedor nuevo.");
      return;
    }
    setCreandoProveedor(true);
    try {
      const creado = await apiFetch("/api/proveedores/rapido", {
        method: "POST",
        body: JSON.stringify(nuevoProveedor),
      });
      setProveedores((prev) => [...prev, creado].sort((a, b) => a.nombre_empresa.localeCompare(b.nombre_empresa, "es")));
      setItemActual((prev) => ({ ...prev, proveedor_ids: [...prev.proveedor_ids, creado.id] }));
      setNuevoProveedor(nuevoProveedorVacio);
      setFormNuevoProveedorAbierto(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreandoProveedor(false);
    }
  };

  // Prioridad global: solo define el valor por defecto del próximo producto
  // que se agregue — cada producto puede seguir cambiando la suya propia en
  // el selector "Prioridad" del formulario, sin afectar a los demás.
  const cambiarPrioridadGlobal = (valor) => {
    setPrioridadGlobal(valor);
    setItemActual((prev) => ({ ...prev, prioridad: valor }));
  };

  const subirImagenItemActual = async (file) => {
    if (!file) return;
    actualizarCampo("subiendoArchivo", true);
    try {
      const nombreArchivo = await subirArchivo(file);
      setItemActual((prev) => ({
        ...prev,
        archivo_referencia: nombreArchivo,
        archivo_nombre: file.name,
        vista_previa: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      actualizarCampo("subiendoArchivo", false);
    }
  };

  const nombreProveedor = (item) => {
    if (!item.proveedor_ids || item.proveedor_ids.length === 0) return "—";
    const nombres = item.proveedor_ids
      .map((id) => proveedores.find((p) => String(p.id) === String(id))?.nombre_empresa)
      .filter(Boolean);
    return nombres.length > 0 ? nombres.join(", ") : "—";
  };

  const agregarOActualizarItem = () => {
    setError("");
    if (!itemActual.descripcion_producto.trim() || !itemActual.cantidad || !itemActual.unidad.trim()) {
      setError("Completa descripción, cantidad y unidad antes de agregarlo a la lista.");
      return;
    }
    // La cantidad llega como texto: "0" no está vacío, así que se revisa el número.
    if (!(Number(itemActual.cantidad) > 0)) {
      setError("La cantidad tiene que ser mayor que 0.");
      return;
    }

    // La cantidad va en su propio campo: si la escribieron en el nombre ("100 metros de cable"), se pasa
    // a Cantidad y Unidad y se pide revisar antes de agregar.
    const conCantidad = separarCantidadDelNombre(itemActual.descripcion_producto);
    if (conCantidad) {
      setItemActual((prev) => ({
        ...prev,
        descripcion_producto: conCantidad.limpio,
        cantidad: prev.cantidad || conCantidad.cantidad,
        unidad: prev.unidad || (UNIDADES.includes(conCantidad.unidad) ? conCantidad.unidad : ""),
      }));
      setError(
        `El nombre traía la cantidad ("${conCantidad.textoQuitado}"). La quitamos del nombre y la pasamos a los campos Cantidad y Unidad: revisa y vuelve a tocar "Agregar a la lista".`
      );
      return;
    }

    if (editandoId !== null) {
      setItemsGuardados((prev) =>
        prev.map((it) => (it._id === editandoId ? { ...itemActual, _id: editandoId } : it))
      );
      setEditandoId(null);
    } else {
      setItemsGuardados((prev) => [...prev, { ...itemActual, _id: proximoId.current++ }]);
    }

    // Solo se limpia el sub-formulario del producto — Área, Edificio y
    // Descripción del requerimiento son datos de toda la solicitud y deben
    // quedarse igual para el siguiente producto que se agregue.
    setItemActual(filaVacia(prioridadGlobal));
  };

  const editarItem = (id) => {
    const item = itemsGuardados.find((it) => it._id === id);
    if (!item) return;
    setItemActual({ ...item });
    setPrioridadGlobal(item.prioridad);
    setEditandoId(id);
  };

  const cancelarEdicionItem = () => {
    setEditandoId(null);
    setItemActual(filaVacia(prioridadGlobal));
  };

  const quitarItem = (id) => {
    setItemsGuardados((prev) => prev.filter((it) => it._id !== id));
    if (editandoId === id) cancelarEdicionItem();
  };

  const soltarArchivo = (e) => {
    e.preventDefault();
    setArrastrandoArchivo(false);
    const file = e.dataTransfer.files?.[0];
    if (file) subirImagenItemActual(file);
  };

  const cancelar = () => {
    const hayDatos =
      areaId || edificioIds.length > 0 || descripcionRequerimientoGlobal.trim() || itemsGuardados.length > 0;
    if (hayDatos && !window.confirm("¿Salir sin guardar? Se perderá la información ingresada.")) {
      return;
    }
    navigate("/");
  };

  const enviar = async (e) => {
    e.preventDefault();
    setError("");

    if (!areaId || edificioIds.length === 0 || !descripcionRequerimientoGlobal.trim()) {
      setError("Completa el área, al menos un edificio y la descripción del requerimiento.");
      return;
    }
    if (itemsGuardados.length === 0) {
      setError('Agrega al menos un producto a la lista con "Agregar a la lista".');
      return;
    }

    setEnviando(true);
    try {
      const itemsResueltos = itemsGuardados.map((it) => ({ ...it, cantidad: Number(it.cantidad) }));

      await apiFetch("/api/solicitudes", {
        method: "POST",
        body: JSON.stringify({
          area_id: areaId,
          edificio_ids: edificioIds,
          descripcion_requerimiento: descripcionRequerimientoGlobal,
          items: itemsResueltos,
        }),
      });
      navigate("/");
    } catch (err) {
      setError(err.message);
      setEnviando(false);
    }
  };

  if (cargandoCatalogos) return <Cargando />;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <EncabezadoDetalle
        titulo="📝 Nueva Solicitud"
        subtitulo="Completa la información para crear una nueva solicitud de compra."
        accion={
          <button type="button" onClick={() => setAyudaAbierta((v) => !v)} style={styles.botonAyuda}>
            ❓ ¿Necesitas ayuda?
          </button>
        }
      />
      <div style={{ height: "0.7rem" }} />

      {ayudaAbierta && (
        <div style={styles.panelAyuda}>
          <strong>¿Cómo funciona?</strong>
          <ol style={{ margin: "0.4rem 0 0", paddingLeft: "1.2rem", fontSize: "0.85rem" }}>
            <li>Completa el área, el/los edificio(s) y describe brevemente el requerimiento general.</li>
            <li>Agrega uno o varios productos con su cantidad, unidad y prioridad.</li>
            <li>Cuando termines, envía la solicitud — compras se encargará de cotizar cada producto.</li>
          </ol>
        </div>
      )}

      <Ayuda>
        <div style={styles.aviso}>
          ℹ️ Los pedidos deben realizarse con un mínimo de <strong>dos semanas de anticipación</strong>,
          salvo que se marquen como <strong>Urgente</strong>.
        </div>
      </Ayuda>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardNumero}>1</span>
          <h3 style={styles.cardTitulo}>Información general</h3>
        </div>

        <div className="form-grid" style={styles.filaDoble}>
          <label style={styles.campo}>
            <span>
              Área solicitante <span style={styles.requerido}>*</span>
            </span>
            {areaFijaId ? (
              <>
                <div style={styles.areaFija}>
                  🔒 {usuario.area_nombre || areas.find((a) => String(a.id) === String(areaFijaId))?.nombre || "—"}
                </div>
                <Ayuda>
                <span style={{ fontSize: "0.72rem", color: "#8993a1", fontWeight: 400 }}>
                  Se toma de tu perfil — si no es tu área correcta, pídele al administrador que la corrija en
                  Usuarios.
                </span>
                </Ayuda>
              </>
            ) : (
              <>
                <select value={areaId} onChange={(e) => setAreaId(e.target.value)} style={styles.input}>
                  <option value="">Selecciona...</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nombre}
                    </option>
                  ))}
                </select>
                {esSolicitante && (
                  <span style={{ fontSize: "0.72rem", color: "#b8722a", fontWeight: 400 }}>
                    ⚠️ Tu usuario todavía no tiene un área asignada — pídele al administrador que te la
                    configure en Usuarios antes de enviar la solicitud.
                  </span>
                )}
              </>
            )}
          </label>

          <div style={styles.campo} ref={edificioDropdownRef}>
            <span>
              Edificio(s) <span style={styles.requerido}>*</span>
            </span>
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setEdificioDropdownAbierto((v) => !v)}
                style={styles.comboboxBoton}
              >
                <span style={{ fontWeight: 400, color: edificioIds.length ? "var(--tema-titulo, #0b3c60)" : "#8993a1" }}>
                  {edificioIds.length === 0
                    ? "Selecciona..."
                    : edificios
                        .filter((ed) => edificioIds.includes(ed.id))
                        .map((ed) => ed.nombre)
                        .join(", ")}
                </span>
                <span style={{ color: "#8993a1" }}>{edificioDropdownAbierto ? "▲" : "▼"}</span>
              </button>

              {edificioDropdownAbierto && (
                <div style={styles.comboboxPanel}>
                  {edificios.map((ed) => (
                    <label key={ed.id} style={styles.filaEdificio}>
                      <input
                        type="checkbox"
                        checked={edificioIds.includes(ed.id)}
                        onChange={() => alternarEdificio(ed.id)}
                      />
                      <span style={{ fontWeight: 400 }}>{ed.nombre}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Ayuda>
              <span style={{ fontSize: "0.75rem", color: "#5b6472", fontWeight: 400 }}>
                Marca todos los edificios que puedan estar involucrados — compras decide después a cuál
                va cada artículo de inventario.
              </span>
            </Ayuda>
          </div>
        </div>

        <div style={styles.filaDescPrioridad}>
          <label style={{ ...styles.campo, flex: 2, minWidth: 240 }}>
            <span>
              Descripción del requerimiento (global) <span style={styles.requerido}>*</span>
            </span>
            <textarea
              ref={descripcionRequerimientoRef}
              value={descripcionRequerimientoGlobal}
              onChange={(e) => setDescripcionRequerimientoGlobal(e.target.value)}
              placeholder="Ej. Soporte usuarios + instalación de cámaras (administración)"
              rows={2}
              maxLength={1000}
              style={{ ...styles.input, resize: "none", overflowY: "auto", maxHeight: 320 }}
            />
            <span style={styles.contador}>{descripcionRequerimientoGlobal.length}/1000</span>
          </label>

          <div style={{ ...styles.campo, flex: 1, minWidth: 200 }}>
            {/* La prioridad es de cada producto: este selector solo define la del
                próximo que se agregue (antes decía solo "Prioridad" y parecía
                aplicar a toda la solicitud). */}
            Prioridad de los productos que agregues
            <div style={styles.togglePrioridad}>
              <button
                type="button"
                onClick={() => cambiarPrioridadGlobal("normal")}
                style={prioridadGlobal === "normal" ? styles.pillNormalActiva : styles.pillBoton}
              >
                🔵 Normal
              </button>
              <button
                type="button"
                onClick={() => cambiarPrioridadGlobal("urgente")}
                style={prioridadGlobal === "urgente" ? styles.pillUrgenteActiva : styles.pillBoton}
              >
                🔴 Urgente
              </button>
            </div>
            <Ayuda>
              <span style={{ fontSize: "0.72rem", color: "#8993a1", fontWeight: 400 }}>
                Se aplica a cada producto que agregues desde ahora; los ya agregados no cambian
                (edítalos con ✏️). Los pedidos Urgentes son la excepción.
              </span>
            </Ayuda>
          </div>
        </div>
      </div>

      {/* Formulario para el producto que se está agregando */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardNumero}>2</span>
          <h3 style={styles.cardTitulo}>
            {editandoId !== null ? "Editando producto" : "Agregar producto"}
          </h3>
        </div>

        <div className="form-grid" style={styles.itemGrid}>
          <label style={{ ...styles.campo, gridColumn: "1 / -1" }}>
            <span>
              Descripción del producto <span style={styles.requerido}>*</span>
            </span>
            <textarea
              ref={descripcionProductoRef}
              rows={1}
              placeholder="Ej. Cámara de seguridad IP 4MP"
              value={itemActual.descripcion_producto}
              onChange={(e) => actualizarCampo("descripcion_producto", e.target.value)}
              style={{ ...styles.input, resize: "none", overflow: "hidden" }}
            />
          </label>

          <label style={styles.campo}>
            <span>
              Cantidad <span style={styles.requerido}>*</span>
            </span>
            <input
              type="number"
              min="1"
              placeholder="Ej. 2"
              value={itemActual.cantidad}
              onChange={(e) => actualizarCampo("cantidad", e.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.campo}>
            <span>
              Unidad <span style={styles.requerido}>*</span>
            </span>
            <select
              value={itemActual.unidad}
              onChange={(e) => actualizarCampo("unidad", e.target.value)}
              style={styles.input}
            >
              <option value="">Selecciona...</option>
              {UNIDADES.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>

          <div style={styles.campo} ref={proveedorDropdownRef}>
            Proveedor sugerido (opcional)
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setProveedorDropdownAbierto((v) => !v)}
                style={styles.comboboxBoton}
              >
                <span
                  style={{ fontWeight: 400, color: itemActual.proveedor_ids.length ? "var(--tema-titulo, #0b3c60)" : "#8993a1" }}
                >
                  {itemActual.proveedor_ids.length === 0
                    ? "— Ninguno —"
                    : proveedores
                        .filter((p) => itemActual.proveedor_ids.includes(p.id))
                        .map((p) => p.nombre_empresa)
                        .join(", ")}
                </span>
                <span style={{ color: "#8993a1" }}>{proveedorDropdownAbierto ? "▲" : "▼"}</span>
              </button>

              {proveedorDropdownAbierto && (
                <div style={styles.comboboxPanel}>
                  {proveedores.map((p) => (
                    <label key={p.id} style={styles.filaEdificio}>
                      <input
                        type="checkbox"
                        checked={itemActual.proveedor_ids.includes(p.id)}
                        onChange={() => alternarProveedor(p.id)}
                      />
                      <span style={{ fontWeight: 400 }}>{p.nombre_empresa}</span>
                    </label>
                  ))}

                  <div style={styles.separadorCombobox} />

                  {formNuevoProveedorAbierto ? (
                    <div style={{ padding: "0.4rem 0", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                      <input
                        type="text"
                        placeholder="Nombre de la empresa"
                        value={nuevoProveedor.nombre_empresa}
                        onChange={(e) => setNuevoProveedor((p) => ({ ...p, nombre_empresa: e.target.value }))}
                        style={styles.input}
                      />
                      <input
                        type="text"
                        placeholder="Teléfono (opcional)"
                        value={nuevoProveedor.telefono}
                        onChange={(e) => setNuevoProveedor((p) => ({ ...p, telefono: e.target.value }))}
                        style={styles.input}
                      />
                      <input
                        type="email"
                        placeholder="Correo (opcional)"
                        value={nuevoProveedor.correo}
                        onChange={(e) => setNuevoProveedor((p) => ({ ...p, correo: e.target.value }))}
                        style={styles.input}
                      />
                      <button
                        type="button"
                        disabled={creandoProveedor}
                        onClick={crearProveedorRapido}
                        style={styles.botonCrearProveedor}
                      >
                        {creandoProveedor ? "Creando..." : "Crear y agregar"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setFormNuevoProveedorAbierto(true)}
                      style={{ ...styles.linkSeleccionar, display: "block", padding: "0.3rem 0" }}
                    >
                      + Otro (agregar proveedor nuevo)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <label style={{ ...styles.campo, gridColumn: "1 / -1" }}>
            Especificaciones / detalles (opcional)
            <textarea
              ref={descripcionDetalladaRef}
              value={itemActual.descripcion_detallada}
              onChange={(e) => actualizarCampo("descripcion_detallada", e.target.value)}
              placeholder="Modelo, marca, características, color, etc."
              rows={2}
              maxLength={1000}
              style={{ ...styles.input, resize: "none", overflowY: "auto", maxHeight: 320 }}
            />
            <span style={styles.contador}>{itemActual.descripcion_detallada.length}/1000</span>
          </label>

          <div style={{ ...styles.campo, gridColumn: "1 / -1" }}>
            Adjuntar imagen o archivo (opcional)
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setArrastrandoArchivo(true);
              }}
              onDragLeave={() => setArrastrandoArchivo(false)}
              onDrop={soltarArchivo}
              style={{ ...styles.zonaArchivo, ...(arrastrandoArchivo ? styles.zonaArchivoActiva : {}) }}
            >
              <input
                ref={archivoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  subirImagenItemActual(e.target.files[0]);
                  e.target.value = "";
                }}
              />
              <span style={{ fontSize: "1.1rem" }}>📎</span>
              <span style={{ fontSize: "0.85rem", fontWeight: 400 }}>
                Arrastra un archivo aquí o{" "}
                <button
                  type="button"
                  onClick={() => archivoInputRef.current?.click()}
                  style={styles.linkSeleccionar}
                >
                  selecciona uno
                </button>
              </span>
              <span style={{ fontSize: "0.72rem", color: "#8993a1", fontWeight: 400 }}>JPG, PNG, PDF (máx. 10MB)</span>
              {itemActual.subiendoArchivo && <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>Subiendo...</span>}
            </div>
            {itemActual.archivo_referencia && !itemActual.subiendoArchivo && (
              <div style={styles.adjuntoListo}>
                <AdjuntoProducto item={itemActual} />
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#2f7a54" }}>
                  ✓ Adjuntado{itemActual.archivo_nombre ? `: ${itemActual.archivo_nombre}` : ""}
                </span>
                <button
                  type="button"
                  style={styles.linkSeleccionar}
                  onClick={() =>
                    setItemActual((prev) => ({ ...prev, archivo_referencia: "", archivo_nombre: "", vista_previa: "" }))
                  }
                >
                  Quitar
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: "0.7rem" }}>
          <button type="button" onClick={agregarOActualizarItem} style={styles.botonAgregar}>
            {editandoId !== null ? "Guardar cambios" : "+ Agregar a la lista"}
          </button>{" "}
          {editandoId !== null && (
            <button type="button" onClick={cancelarEdicionItem}>
              Cancelar edición
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardNumero}>3</span>
          <h3 style={styles.cardTitulo}>Productos agregados ({itemsGuardados.length})</h3>
        </div>
        {itemsGuardados.length === 0 ? (
          <div style={styles.vacioProductos}>
            <span style={{ fontSize: "1.8rem" }}>📦</span>
            <p style={{ margin: "0.4rem 0 0", fontWeight: 600, color: "#5b6472" }}>
              Aún no has agregado productos
            </p>
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.82rem", color: "#8993a1" }}>
              Agrega productos usando el formulario anterior.
            </p>
          </div>
        ) : (
          <div className="table-scroll" style={styles.tablaWrap}>
            <table style={styles.tabla}>
              <thead>
                <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Producto</th>
                  <th style={styles.th}>Cant.</th>
                  <th style={styles.th}>Unidad</th>
                  <th style={styles.th}>Prioridad</th>
                  <th style={styles.th}>Proveedor sugerido</th>
                  <th style={styles.th}>Especificaciones</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {itemsGuardados.map((it, i) => (
                  <tr
                    key={it._id}
                    style={{
                      borderBottom: "1px solid #eef1f3",
                      background: editandoId === it._id ? "#f2e3d1" : i % 2 === 1 ? "#fafbfc" : "transparent",
                    }}
                  >
                    <td style={styles.td}>{i + 1}</td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                        {it.archivo_referencia && <AdjuntoProducto item={it} />}
                        <span>{it.descripcion_producto}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{it.cantidad}</td>
                    <td style={styles.td}>{it.unidad || "—"}</td>
                    <td style={styles.td}>{it.prioridad === "urgente" ? "🔴 Urgente" : "🔵 Normal"}</td>
                    <td style={styles.td}>{nombreProveedor(it)}</td>
                    <td style={{ ...styles.td, color: "#5b6472", maxWidth: 260 }}>{it.descripcion_detallada || "—"}</td>
                    <td style={styles.td}>
                      <button type="button" title="Editar producto" onClick={() => editarItem(it._id)} style={styles.botonFilaTabla}>
                        ✏️
                      </button>{" "}
                      <button type="button" title="Quitar de la lista" onClick={() => quitarItem(it._id)} style={styles.botonFilaTabla}>
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {itemsGuardados.length > 0 && (
          <div style={styles.resumen}>
            <div style={styles.resumenItem}>
              <span style={styles.resumenEtiqueta}>Área</span>
              <strong>{areas.find((a) => String(a.id) === String(areaId))?.nombre || usuario?.area_nombre || "—"}</strong>
            </div>
            <div style={styles.resumenItem}>
              <span style={styles.resumenEtiqueta}>Edificio(s)</span>
              <strong>
                {edificios
                  .filter((ed) => edificioIds.includes(ed.id))
                  .map((ed) => ed.nombre)
                  .join(", ") || "—"}
              </strong>
            </div>
            <div style={styles.resumenItem}>
              <span style={styles.resumenEtiqueta}>Prioridad</span>
              {/* Se cuenta sobre los productos ya agregados, no sobre el selector. */}
              <strong>
                {(() => {
                  const urgentes = itemsGuardados.filter((it) => it.prioridad === "urgente").length;
                  const normales = itemsGuardados.length - urgentes;
                  if (itemsGuardados.length === 0) return "—";
                  if (urgentes === 0) return "🔵 Normal";
                  if (normales === 0) return "🔴 Urgente";
                  return `🔴 ${urgentes} urgente${urgentes === 1 ? "" : "s"} · 🔵 ${normales} normal${normales === 1 ? "" : "es"}`;
                })()}
              </strong>
            </div>
            <div style={styles.resumenItem}>
              <span style={styles.resumenEtiqueta}>Productos agregados</span>
              <strong>{itemsGuardados.length}</strong>
            </div>
          </div>
        )}
      </div>

      <div style={styles.barraAcciones}>
        <button type="button" onClick={cancelar} style={styles.botonCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          disabled={enviando || itemsGuardados.length === 0}
          onClick={enviar}
          style={{
            ...styles.botonEnviar,
            ...(enviando || itemsGuardados.length === 0 ? styles.botonEnviarDeshabilitado : {}),
          }}
        >
          {enviando
            ? "Enviando..."
            : `📤 Enviar solicitud · ${itemsGuardados.length} producto${itemsGuardados.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

const styles = {
  botonAyuda: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "var(--tema-primario, #2f6690)",
    borderRadius: 8,
    padding: "0.4rem 0.8rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  panelAyuda: {
    background: "#f7f8fa",
    border: "1px solid #e2e6ea",
    borderRadius: 8,
    padding: "0.75rem 1rem",
    fontSize: "0.85rem",
    margin: "0.75rem 0 0",
  },
  aviso: {
    background: "var(--tema-suave, #dceaf5)",
    borderLeft: "3px solid var(--tema-primario, #2f6690)",
    borderRadius: "0 6px 6px 0",
    padding: "0.75rem 1rem",
    fontSize: "0.85rem",
    margin: "0.6rem 0 0.8rem",
  },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.75rem 1rem",
    marginBottom: "0.7rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.05)",
    ...tarjetaTema,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "0.65rem",
    paddingBottom: "0.45rem",
    borderBottom: `1px solid ${TEMA.linea}`,
  },
  cardNumero: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: TEMA.dorado,
    color: TEMA.azulOscuro,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  cardIcono: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: "var(--tema-suave, #dceaf5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    flexShrink: 0,
  },
  cardTitulo: { margin: 0, fontSize: "0.9rem", fontWeight: 800, color: TEMA.azulOscuro, textTransform: "uppercase", letterSpacing: "0.03em" },
  requerido: { color: "#a3402f" },
  fila: { marginBottom: "0.75rem" },
  filaDoble: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.6rem",
    marginBottom: "0.55rem",
  },
  filaDescPrioridad: {
    display: "flex",
    gap: "0.85rem",
    flexWrap: "wrap",
  },
  campo: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    fontSize: "0.82rem",
    fontWeight: 600,
  },
  contador: { fontSize: "0.72rem", color: "#8993a1", fontWeight: 400, textAlign: "right" },
  input: {
    fontWeight: 400,
    fontSize: "0.85rem",
    padding: "0.4rem 0.55rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  areaFija: {
    fontWeight: 400,
    fontSize: "0.85rem",
    padding: "0.4rem 0.55rem",
    border: "1px solid #eef1f3",
    borderRadius: 6,
    background: "#f7f8fa",
    color: "var(--tema-titulo, #0b3c60)",
  },
  togglePrioridad: { display: "flex", gap: "0.5rem" },
  pillBoton: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    padding: "0.42rem 0.6rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    background: "#fff",
    color: "#5b6472",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
  },
  pillNormalActiva: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    padding: "0.42rem 0.6rem",
    border: "1px solid var(--tema-primario, #2f6690)",
    borderRadius: 8,
    background: "var(--tema-suave, #dceaf5)",
    color: "var(--tema-primario, #2f6690)",
    fontWeight: 700,
    fontSize: "0.85rem",
    cursor: "pointer",
  },
  pillUrgenteActiva: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    padding: "0.42rem 0.6rem",
    border: "1px solid #a3402f",
    borderRadius: 8,
    background: "#f7ded9",
    color: "#a3402f",
    fontWeight: 700,
    fontSize: "0.85rem",
    cursor: "pointer",
  },
  comboboxBoton: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.85rem",
    padding: "0.4rem 0.55rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    boxSizing: "border-box",
  },
  comboboxPanel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    minWidth: 240,
    zIndex: 20,
    background: "#fff",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    maxHeight: 220,
    overflowY: "auto",
    boxShadow: "0 4px 14px rgba(27,42,74,0.15)",
  },
  filaEdificio: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.25rem 0",
    fontSize: "0.85rem",
  },
  separadorCombobox: {
    borderTop: "1px solid #eef1f3",
    margin: "0.3rem 0",
  },
  botonCrearProveedor: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "0.4rem 0.7rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  itemGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "0.55rem 0.65rem",
  },
  zonaArchivo: {
    border: "1.5px dashed #b8d2e8",
    borderRadius: 8,
    background: "#f7fafc",
    padding: "0.45rem 0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  adjuntoListo: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    marginTop: "0.5rem",
    padding: "0.45rem 0.7rem",
    background: "#eef7f1",
    border: "1px solid #cfe6d8",
    borderRadius: 8,
    fontWeight: 400,
  },
  zonaArchivoActiva: {
    border: "1.5px dashed var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #eef4fb)",
  },
  linkSeleccionar: {
    background: "none",
    border: "none",
    color: "var(--tema-primario, #2f6690)",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  vacioProductos: {
    textAlign: "center",
    padding: "2rem 1rem",
    border: "1px dashed #d3d9df",
    borderRadius: 8,
    background: "#f7f8fa",
  },
  tablaWrap: {
    border: "1px solid #eef1f3",
    borderRadius: 10,
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: {
    padding: "0.5rem",
    border: "1px solid #d3d9df",
    fontSize: "0.76rem",
    ...thTema,
  },
  td: { padding: "0.4rem 0.5rem", textAlign: "left" },
  resumen: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem 2rem",
    marginTop: "0.7rem",
    padding: "0.55rem 0.9rem",
    background: "#f3f6fb",
    border: "1px solid #dfe6f1",
    borderRadius: 8,
    fontSize: "0.85rem",
  },
  resumenItem: { display: "flex", flexDirection: "column", gap: "0.1rem" },
  resumenEtiqueta: { fontSize: "0.7rem", color: "#8993a1", fontWeight: 600, textTransform: "uppercase" },
  botonFilaTabla: {
    border: "1px solid #d3d9df",
    background: "#fff",
    borderRadius: 6,
    padding: "0.25rem 0.55rem",
    fontSize: "0.78rem",
    cursor: "pointer",
  },
  botonAgregar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  barraAcciones: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "0.9rem",
    flexWrap: "wrap",
    gap: "0.75rem",
  },
  botonCancelar: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.6rem 1.3rem",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  botonEnviar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.6rem 1.5rem",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  botonEnviarDeshabilitado: {
    background: "#c7ccd3",
    cursor: "not-allowed",
  },
};

export default NuevaSolicitud;
